import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { obterPerfilPorSlug, PERFIS_PRESENTES, PerfilPresente } from 'config/perfisPresentes';
import {
  listarCatalogoProdutos,
  ProdutoAfiliado,
  ProdutoAfiliadoFilters,
} from 'services/produtosAfiliados';

export interface FaixaPreco {
  slug: string;
  label: string;
  minPreco?: number;
  maxPreco?: number;
}

export const FAIXAS_PRECO: FaixaPreco[] = [
  { slug: 'todas', label: 'Todos' },
  { slug: 'ate-50', label: 'Até R$50', maxPreco: 50 },
  { slug: '50-150', label: 'R$50~150', minPreco: 50, maxPreco: 150 },
  { slug: '150-300', label: 'R$150~300', minPreco: 150, maxPreco: 300 },
  { slug: '300-1000', label: 'R$300~1000', minPreco: 300, maxPreco: 1000 },
  { slug: '1000-mais', label: 'R$1000+', minPreco: 1000 },
];

const PAGE_SIZE = 24;

/** Pseudo-perfil que mostra todos os produtos classificados (qualquer tag perfil-*) */
export const PERFIL_TODOS: PerfilPresente = {
  slug: 'todos',
  tag: 'perfil-',
  label: 'Todos',
  emoji: '🎁',
  descricao: 'Todas as ideias de presentes',
};

/** Pseudo-perfil: quedas de preço detectadas hoje (vs. o refresh de ontem) */
export const PERFIL_OFERTAS_DIA: PerfilPresente = {
  slug: 'ofertas-dia',
  tag: 'oferta-dia',
  label: 'Ofertas do dia',
  emoji: '🔥',
  descricao: 'Preços que caíram hoje',
};

/** Pseudo-perfil: quedas vs. o pico dos últimos 7 dias */
export const PERFIL_OFERTAS_SEMANA: PerfilPresente = {
  slug: 'ofertas-semana',
  tag: 'oferta-semana',
  label: 'Ofertas semana',
  emoji: '🗓️',
  descricao: 'Quedas vs. o pico dos últimos 7 dias',
};

/** Afiliados (fontes) disponíveis para o filtro — chave = origemDados no backend */
export interface AfiliadoFonte {
  chave: string;
  label: string;
  /** Logo oficial em public/afiliados/<arquivo>.png (fallback = inicial em `cor`) */
  logo: string;
  cor: string;
}

export const AFILIADOS: AfiliadoFonte[] = [
  { chave: 'magalu', label: 'Magalu', logo: '/afiliados/magalu.png', cor: '#0086FF' },
  { chave: 'meli', label: 'Mercado Livre', logo: '/afiliados/mercadolivre.png', cor: '#FFE600' },
  { chave: 'amazon', label: 'Amazon', logo: '/afiliados/amazon.png', cor: '#FF9900' },
  { chave: 'aliexpress', label: 'AliExpress', logo: '/afiliados/aliexpress.png', cor: '#E62E04' },
  { chave: 'shopee', label: 'Shopee', logo: '/afiliados/shopee.png', cor: '#EE4D2D' },
];

/** Grupos canônicos de categoria "de fato" (tag cat-<slug> no backend) */
export interface GrupoCategoria {
  slug: string;
  label: string;
  emoji: string;
}

export const CATEGORIAS_GRUPOS: GrupoCategoria[] = [
  { slug: 'beleza', label: 'Beleza & Cuidados', emoji: '💄' },
  { slug: 'cozinha', label: 'Cozinha & Eletro', emoji: '🍳' },
  { slug: 'casa', label: 'Casa & Decoração', emoji: '🏠' },
  { slug: 'eletronicos', label: 'Eletrônicos & Áudio', emoji: '🎧' },
  { slug: 'brinquedos', label: 'Brinquedos', emoji: '🧸' },
  { slug: 'bebe', label: 'Bebê', emoji: '👶' },
  { slug: 'games', label: 'Games', emoji: '🎮' },
  { slug: 'esporte', label: 'Esporte & Fitness', emoji: '🏋️' },
  { slug: 'moda', label: 'Moda & Acessórios', emoji: '👜' },
  { slug: 'ferramentas', label: 'Ferramentas', emoji: '🔧' },
  { slug: 'pet', label: 'Pet', emoji: '🐾' },
  { slug: 'papelaria', label: 'Papelaria & Arte', emoji: '🎨' },
  { slug: 'automotivo', label: 'Automotivo', emoji: '🚗' },
  { slug: 'musica', label: 'Instrumentos', emoji: '🎸' },
  { slug: 'saude', label: 'Saúde', emoji: '💊' },
];

export const useIdeiasPresentesRules = () => {
  const { perfilSlug } = useParams<{ perfilSlug?: string }>();
  const navigate = useNavigate();

  const perfilSelecionado = useMemo(() => {
    if (perfilSlug === PERFIL_TODOS.slug) return PERFIL_TODOS;
    if (perfilSlug === PERFIL_OFERTAS_DIA.slug) return PERFIL_OFERTAS_DIA;
    if (perfilSlug === PERFIL_OFERTAS_SEMANA.slug) return PERFIL_OFERTAS_SEMANA;
    return obterPerfilPorSlug(perfilSlug);
  }, [perfilSlug]);

  // Os chips de Ofertas só aparecem quando o refresh marcou quedas de preço
  const [temOfertasDia, setTemOfertasDia] = useState(false);
  const [temOfertasSemana, setTemOfertasSemana] = useState(false);
  useEffect(() => {
    let ativo = true;
    void Promise.all([
      listarCatalogoProdutos({ tag: PERFIL_OFERTAS_DIA.tag, pageSize: 1 }),
      listarCatalogoProdutos({ tag: PERFIL_OFERTAS_SEMANA.tag, pageSize: 1 }),
    ])
      .then(([dia, semana]) => {
        if (!ativo) return;
        setTemOfertasDia(dia.total > 0);
        setTemOfertasSemana(semana.total > 0);
      })
      .catch(() => {
        if (!ativo) return;
        setTemOfertasDia(false);
        setTemOfertasSemana(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  // Filtro de afiliados (vazio = todos). Selecionar uma fonte filtra; "Todos" limpa.
  const [fontesSelecionadas, setFontesSelecionadas] = useState<string[]>([]);
  const alternarFonte = useCallback((chave: string) => {
    setFontesSelecionadas((atuais) =>
      atuais.includes(chave) ? atuais.filter((c) => c !== chave) : [...atuais, chave]
    );
  }, []);
  const limparFontes = useCallback(() => setFontesSelecionadas([]), []);

  const [faixaSlug, setFaixaSlug] = useState<string>('todas');
  // Filtro por categoria "de fato" (grupo canônico). Vazio = todas.
  const [catGrupo, setCatGrupo] = useState<string>('');

  // Ordenação da vitrine ('relevancia' = padrão curado | 'preco-asc' | 'preco-desc')
  const [ordem, setOrdem] = useState<string>('relevancia');

  // Filtro por texto livre (debounce p/ não disparar request a cada tecla)
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBuscaAplicada(busca.trim()), 400);
    return () => clearTimeout(t);
  }, [busca]);

  const [produtos, setProdutos] = useState<ProdutoAfiliado[]>([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const faixaSelecionada = useMemo(
    () => FAIXAS_PRECO.find((faixa) => faixa.slug === faixaSlug) ?? FAIXAS_PRECO[0],
    [faixaSlug]
  );

  const buscarPagina = useCallback(
    async (paginaAlvo: number, acumular: boolean) => {
      if (!perfilSelecionado) return;

      setCarregando(true);
      setErro(null);
      try {
        const filtros: ProdutoAfiliadoFilters = {
          tag: perfilSelecionado.tag,
          page: paginaAlvo,
          pageSize: PAGE_SIZE,
        };
        if (typeof faixaSelecionada.minPreco === 'number') filtros.minPreco = faixaSelecionada.minPreco;
        if (typeof faixaSelecionada.maxPreco === 'number') filtros.maxPreco = faixaSelecionada.maxPreco;
        if (fontesSelecionadas.length > 0) filtros.fontes = fontesSelecionadas.join(',');
        if (catGrupo) filtros.catGrupo = catGrupo;
        if (ordem && ordem !== 'relevancia') filtros.ordem = ordem;
        if (buscaAplicada) filtros.search = buscaAplicada;

        const resposta = await listarCatalogoProdutos(filtros);
        setProdutos((atuais) => (acumular ? [...atuais, ...resposta.produtos] : resposta.produtos));
        setPagina(resposta.page);
        setTotalPaginas(resposta.totalPages);
        setTotal(resposta.total);
      } catch {
        setErro('Não foi possível carregar as ideias agora. Tente novamente em instantes.');
      } finally {
        setCarregando(false);
      }
    },
    [perfilSelecionado, faixaSelecionada, fontesSelecionadas, catGrupo, ordem, buscaAplicada]
  );

  // Ao pesquisar texto, busca em TODAS as categorias (mantém parceiros e preço)
  useEffect(() => {
    if (buscaAplicada && perfilSelecionado && perfilSelecionado.slug !== PERFIL_TODOS.slug) {
      navigate(`/presentes/${PERFIL_TODOS.slug}`);
    }
  }, [buscaAplicada, perfilSelecionado, navigate]);

  // Recarrega ao trocar perfil, faixa de preço ou afiliados
  useEffect(() => {
    setProdutos([]);
    setPagina(1);
    if (perfilSelecionado) {
      void buscarPagina(1, false);
    }
  }, [perfilSelecionado, faixaSelecionada, buscarPagina]);

  const carregarMais = useCallback(() => {
    if (carregando || pagina >= totalPaginas) return;
    void buscarPagina(pagina + 1, true);
  }, [buscarPagina, carregando, pagina, totalPaginas]);

  const selecionarPerfil = useCallback(
    (slug: string) => {
      setFaixaSlug('todas');
      navigate(`/presentes/${slug}`);
    },
    [navigate]
  );

  // Aplica de uma vez categoria + lojas + faixa (usado pelo painel de filtros).
  // Diferente de selecionarPerfil, não reseta a faixa — respeita o que o usuário escolheu.
  const aplicarFiltros = useCallback(
    (opts: { perfilSlug: string; fontes: string[]; faixaSlug: string; catGrupo: string }) => {
      setFontesSelecionadas(opts.fontes);
      setFaixaSlug(opts.faixaSlug);
      setCatGrupo(opts.catGrupo);
      navigate(`/presentes/${opts.perfilSlug}`);
    },
    [navigate]
  );

  return {
    perfis: PERFIS_PRESENTES,
    temOfertasDia,
    temOfertasSemana,
    afiliados: AFILIADOS,
    fontesSelecionadas,
    alternarFonte,
    limparFontes,
    perfilSelecionado,
    produtos,
    total,
    carregando,
    erro,
    temMais: pagina < totalPaginas,
    faixaSlug,
    setFaixaSlug,
    catGrupo,
    ordem,
    setOrdem,
    busca,
    setBusca,
    carregarMais,
    selecionarPerfil,
    aplicarFiltros,
  };
};
